/**
 * FeatureRenderer - Handles feature (coordinate frame) visualization
 */
import * as THREE from 'three';
import { LabelFactory } from './helpers/LabelFactory.js';

export class FeatureRenderer {
  constructor(scene) {
    this.scene = scene;
    this.featureMeshes = [];
    this.features = [];
    this.visible = false;
  }

  /**
   * Visualize features (coordinate frames)
   * @param {Array<Object>} features - Array of feature objects
   */
  visualize(features) {
    // Clear existing features
    this.clear();
    
    this.features = features;

    // Create feature visualizations
    features.forEach((feature, index) => {
      this.createFeatureMesh(feature, index);
    });

    // Set initial visibility
    this.setVisible(this.visible);
  }

  /**
   * Create a 3D visualization for a feature (coordinate frame)
   * @param {Object} feature - Feature data
   * @param {number} index - Feature index
   */
  createFeatureMesh(feature, index) {
    const group = new THREE.Group();
    
    // Position the feature (convert robot coords to Three.js)
    group.position.set(
      feature.position.x,
      feature.position.z,   // Z becomes Y
      -feature.position.y   // Y becomes -Z
    );
    
    // Apply rotation
    group.rotation.set(feature.rotation.rx, feature.rotation.rz, -feature.rotation.ry);
    
    // Create coordinate frame axes
    const axisLength = 0.15;
    const axisRadius = 0.003;
    
    // X-axis (red)
    const xGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const xMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const xAxis = new THREE.Mesh(xGeometry, xMaterial);
    xAxis.rotation.z = -Math.PI / 2;
    xAxis.position.x = axisLength / 2;
    group.add(xAxis);
    
    // Y-axis (green) - points in -Z direction
    const yGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const yMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const yAxis = new THREE.Mesh(yGeometry, yMaterial);
    yAxis.rotation.x = Math.PI / 2;
    yAxis.position.z = -axisLength / 2;
    group.add(yAxis);

    // Z-axis (blue) - points up
    const zGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const zMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
    const zAxis = new THREE.Mesh(zGeometry, zMaterial);
    zAxis.position.y = axisLength / 2;
    group.add(zAxis);
    
    // Add sphere at origin
    const sphereGeometry = new THREE.SphereGeometry(0.02, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({ 
      color: feature.isBase ? 0xffffff : 0xffaa00,
      transparent: true,
      opacity: 0.8
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    group.add(sphere);
    
    // Add label
    const label = LabelFactory.createFeatureLabel(feature.name);
    label.position.set(0, 0.12, 0);
    group.add(label);
    
    // Store reference
    group.userData = { feature, index };
    
    this.scene.add(group);
    this.featureMeshes.push(group);
    
    return group;
  }

  /**
   * Toggle feature visibility
   * @param {boolean} visible - Whether features should be visible
   */
  setVisible(visible) {
    this.visible = visible;
    this.featureMeshes.forEach(mesh => {
      mesh.visible = visible;
    });
  }

  /**
   * Get selectable meshes for raycasting
   * @returns {Array} Array of feature meshes
   */
  getSelectableMeshes() {
    return this.featureMeshes;
  }

  /**
   * Clear all feature visualizations
   */
  clear() {
    this.featureMeshes.forEach(group => {
      this.scene.remove(group);
      group.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
    this.featureMeshes = [];
    this.features = [];
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.clear();
  }
}
